import React, { useEffect, useState, useRef, useCallback } from "react";
import "../App.css";
import "../styles/main.css";
import styles from "../styles/headsets.module.css";
import { getHeadsetsForAdmin, getTrainingsListWithPermissions, remoteStart, getHeadsets } from "./helpers/requests.tsx";
import AddHeadsetModal from "./modals/addHeadsetModal.tsx";
import ChangeHeadsetDataModal from "./modals/changeHeadsetDataModal.tsx";
import ConfirmDeleteHeadsetModal from "./modals/confirmDeleteHeadsetModal.tsx";
import RemoteStartModal from "./modals/remoteStartModal.tsx";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAuth } from "../firebase/auth.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

const Headsets = () => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const params = useParams();
  const { headsetId } = params;
  // We try to use headquarterId from params, but if it's missing (Headset Manager), we need to resolve it.
  const [headquarterId, setHeadquarterId] = useState(params.headquarterId); 

  const { userId, userRole } = useAuth();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [headsets, setHeadsets] = useState<any[]>([]); 
  const [selectedHeadset, setSelectedHeadset] = useState<any>(null); 
  const [showCreateHeadsetModal, setShowCreateHeadsetModal] = useState(false);
  const [showChangeHeadsetDataModal, setShowChangeHeadsetDataModal] =
    useState(false);
  const [showConfirmDeleteHeadsetModal, setShowConfirmDeleteHeadsetModal] =
    useState(false);
  const [selectedLicenseIdToDelete, setSelectedLicenseIdToDelete] = useState<string | null>(null);
  const [selectedDeviceIdToDelete, setSelectedDeviceIdToDelete] = useState<string | null>(null);
  
  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);

  const [showRemoteStartModal, setShowRemoteStartModal] = useState(false);
  const [pendingTraining, setPendingTraining] = useState<any | null>(null);

  const { t } = useTranslation();

  // Filter hook
  const {
    filteredData: filteredHeadsets,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(headsets || []);

  // Resolve Headquarter ID if missing
  useEffect(() => {
    const resolveHeadquarterId = async () => {
      if (!headquarterId && headsetId) {
        // Temporary workaround: Fetch all headsets (if permission allows) or use a specific lookup if available.
        // Since we don't have a direct "getHeadset" endpoint exposed here easily without modifying requests heavily,
        // we will try to rely on the fact that if we are here, we likely have access.
        // We'll import 'getHeadsets' dynamically to avoid circular deps if any, or just use the imported one.
        try {
            const { getHeadsets } = require("./helpers/requests.tsx");
            const allHeadsets = await getHeadsets();
            const current = allHeadsets.find((h: any) => h.id === headsetId);
            if (current) {
                setHeadquarterId(current.headquarterId);
            }
        } catch (e) {
            console.error("Failed to resolve headquarterId", e);
        }
      }
    };
    if (!headquarterId && userRole !== 'headsetAdmin') {
        resolveHeadquarterId();
    }
  }, [headquarterId, headsetId, userRole]);

  const refreshHeadsets = useCallback(async () => {
    // If Headset Manager, load their headsets regardless of HQ
    if (userRole === 'headsetAdmin' && userId) {
      setIsLoading(true);
      try {
        const data = await getHeadsetsForAdmin(userId);
        setHeadsets(data);
      } catch (error) {
        console.error("Error getting headsets for admin:", error);
      }
      setIsLoading(false);
      return;
    }

    // Global Admin: Load ALL headsets if no HQ is selected
    if (userRole === 'admin' && !headquarterId) {
       setIsLoading(true);
       try {
         const data = await getHeadsets();
         setHeadsets(data);
       } catch (error) {
         console.error("Error getting all headsets:", error);
       }
       setIsLoading(false);
       return;
    }

    // Otherwise, we need a headquarterId
    if (!headquarterId) {
        console.log("No headquarterId, skipping fetch.");
        return;
    }
    
    setIsLoading(true);
    try {
      const data = await getHeadsets(headquarterId); // Use unified API
      setHeadsets(data);
    } catch (error) {
      console.error("Error getting headsets:", error);
    }
    setIsLoading(false);
  }, [headquarterId, userId, userRole]);

  // Fetch trainings based on selected headset's license
  useEffect(() => {
    const fetchHeadsetTrainings = async () => {
      if (!selectedHeadset) {
        setTrainingsList([]);
        return;
      }

      if (!selectedHeadset.licenseId) {
        setTrainingsList([]); // No license = no trainings
        return;
      }

      setIsLoadingTrainings(true);
      try {
        const trainings = await getTrainingsListWithPermissions(
          selectedHeadset.licenseId,
          "license",
          false
        );
        setTrainingsList(trainings);
      } catch (error) {
        console.error("Error fetching trainings for headset license:", error);
        setTrainingsList([]);
      } finally {
        setIsLoadingTrainings(false);
      }
    };

    fetchHeadsetTrainings();
  }, [selectedHeadset]);

  useEffect(() => {
    // If we have HQ ID or if we are headset manager OR GLOBAL ADMIN, refresh headsets
    if (headquarterId || userRole === 'headsetAdmin' || userRole === 'admin') {
      refreshHeadsets();
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    const handleHeadsetAdded = () => {
      refreshHeadsets();
    };

    window.addEventListener('headsetAdded', handleHeadsetAdded); // Keeping 'headsetAdded' event name for now if used elsewhere, or update if possible. Let's keep it to minimize breakage.

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener('headsetAdded', handleHeadsetAdded);
    };
  }, [refreshHeadsets, headquarterId, userRole]);

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };
  
  const handleRemoteStart = async (training: any) => {
    if (!selectedHeadset || !selectedHeadset.deviceId) {
      alert(t("Please_select_a_headset_first"));
      return;
    }
    setPendingTraining(training);
    setShowRemoteStartModal(true);
  };

  const handleRemoteStartConfirm = async (userId: string, logoutAfterTraining: boolean) => {
    if (!selectedHeadset || !selectedHeadset.deviceId || !pendingTraining) return;
    
    try {
      await remoteStart(
        selectedHeadset.deviceId, 
        pendingTraining.id, 
        userId, 
        logoutAfterTraining,
        pendingTraining.manufacturerId,
        pendingTraining.sceneName,
        pendingTraining.name
      );
      setShowRemoteStartModal(false);
      setPendingTraining(null);
    } catch (error) {
      console.error("Error sending remote start command:", error);
    }
  };

  const handleChangeHeadsetData = (updatedHeadset: any) => {
    setHeadsets(
      headsets.map((h) =>
        h.id === updatedHeadset.id ? updatedHeadset : h
      )
    );
    setSelectedHeadset(updatedHeadset); 
    setShowChangeHeadsetDataModal(false);
  };

  const handleDeleteHeadsetSubmit = () => {
    setShowConfirmDeleteHeadsetModal(false);
    setSelectedLicenseIdToDelete(null);
    setSelectedDeviceIdToDelete(null);
    setSelectedHeadset(null);
    refreshHeadsets(); 
  };

  const renderAddHeadsetModal = () => {
    return (
      <AddHeadsetModal
        setShowAddHeadsetModal={setShowCreateHeadsetModal}
        headquarterId={headquarterId} 
      />
    );
  };

  const renderChangeHeadsetDataModal = () => {
    return (
      <ChangeHeadsetDataModal
        setShowChangeHeadsetDataModal={setShowChangeHeadsetDataModal}
        headset={selectedHeadset}
        handleChangeHeadsetData={handleChangeHeadsetData}
        headsetId={selectedHeadset?.id} 
      />
    );
  };

  const renderConfirmDeleteHeadsetModal = () => {
    return (
      <ConfirmDeleteHeadsetModal
        setShowConfirmDeleteHeadsetModal={setShowConfirmDeleteHeadsetModal}
        licenseId={selectedLicenseIdToDelete} 
        headsetId={selectedHeadset?.id}
        headsetName={selectedDeviceIdToDelete}
        handleDeleteHeadsetSubmit={handleDeleteHeadsetSubmit}
      />
    );
  };

  const renderRemoteStartModal = () => {
    if (!selectedHeadset) return null;
    return (
      <RemoteStartModal
        setShowModal={setShowRemoteStartModal}
        onConfirm={handleRemoteStartConfirm}
        headquarterId={selectedHeadset.headquarterId}
      />
    );
  };
  
  const groupTrainingsByManufacturer = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      if (!training.permission) return;

      const {
        id,
        name,
        manufacturerId,
        manufacturerName,
        sceneName,
      } = training;
      if (!groupedTrainings[manufacturerId]) {
        groupedTrainings[manufacturerId] = {
          manufacturerName: manufacturerName,
          trainings: [],
        };
      }
      groupedTrainings[manufacturerId].trainings.push({ 
        id, 
        name, 
        manufacturerId, 
        sceneName,
        scenarioName: name // Explicitly adding scenarioName mapping to name
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([manufacturerId, { manufacturerName, trainings }]: [string, any]) => (
            <div key={manufacturerId} className={styles.trainingsGroup}>
              <label className={styles.trainingsGroupTitle}>
                {manufacturerName}
              </label>
              <ul>
                {trainings.map((training) => (
                  <li key={training.id} className={styles.trainingItem}>
                    <span>{training.name}</span>
                    <button
                      className={styles.startButton}
                      onClick={() => handleRemoteStart(training)}
                    >
                      {t("Start_Remotely")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )
        )}
      </>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoading ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">
                      <TableHeaderFilter
                        column="name"
                        title={t("Headset")}
                        data={headsets || []}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                      />
                    </th>
                    {(userRole === 'admin' && !headquarterId) && (
                      <th scope="col">
                        <TableHeaderFilter
                          column="headquarterName"
                          title={t("Headquarter")}
                          data={headsets || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    {(userRole === 'admin' || headquarterId) && (
                      <th scope="col">
                        <TableHeaderFilter
                          column="adminNames"
                          title={t("Headset_Manager")}
                          data={headsets || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    {(userRole === 'admin' || headquarterId) && (
                      <th scope="col">
                        <TableHeaderFilter
                          column="licenseId"
                          title={t("License")}
                          data={headsets || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>
            <div className={styles.tableContent}>
              <table className="table">
                <tbody>
                  {filteredHeadsets.map((headset: any, index: number) => (
                    <tr
                      key={headset.id}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "#D4E5E2" : "transparent",
                      }}
                    >
                      <td>
                        {headset.name || headset.deviceId || "No Name"}
                      </td>
                      {(userRole === 'admin' && !headquarterId) && (
                        <td>{headset.headquarterName || "-"}</td>
                      )}
                      {(userRole === 'admin' || headquarterId) && (
                        <td>
                          {headset.adminNames && headset.adminNames.length > 0
                            ? headset.adminNames.join(", ")
                            : t("No_headset_manager")}
                        </td>
                      )}
                      {(userRole === 'admin' || headquarterId) && (
                         <td>{headset.licenseId || "-"}</td>
                      )}
                      <td className={styles.editButtonWrapper}>
                        <button
                          type="button"
                          className={`btn btn-primary ${styles.detailsButton} ${selectedHeadset?.id === headset.id ? styles.selected : ""}`}
                          onClick={() =>
                            setSelectedHeadset(headset)
                          }
                          style={{
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "3.5vh",
                            maxHeight: "40px",
                            maxWidth: "100%",
                            borderRadius: "10px",
                            fontFamily: "VirtualLabQuickSandMedium",
                            marginLeft: "auto",
                            marginRight: "10px",
                            outline: "none",
                            boxShadow: "none",
                            padding: "0",
                            backgroundColor:
                              selectedHeadset?.id === headset.id
                                ? "#404040"
                                : "#39A686",
                            borderColor:
                              selectedHeadset?.id === headset.id
                                ? "#404040"
                                : "#39A686",
                          }}
                        >
                          {t("Details")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedHeadset ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        {selectedHeadset.name}
                      </div>
                    </div>
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#39A686",
                          borderColor: "#39A686",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          {/* Edit Headset Button */}
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeHeadsetDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_Headset")}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setSelectedLicenseIdToDelete(selectedHeadset.licenseId); 
                              setSelectedDeviceIdToDelete(selectedHeadset.deviceId);
                              setShowConfirmDeleteHeadsetModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Delete.png" />
                            {t("Delete_Headset")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <hr />
                </div>

                <div className={`${styles.tableContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(groupTrainingsByManufacturer(trainingsList))
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>
                {t("Select_headset_to_edit")}
              </p>
            )
            }
          </div>
        </>
      )}
      <div className={styles.adminContainer}>
        {showCreateHeadsetModal && renderAddHeadsetModal()}
        {showChangeHeadsetDataModal && renderChangeHeadsetDataModal()}
        {showConfirmDeleteHeadsetModal && renderConfirmDeleteHeadsetModal()}
        {showRemoteStartModal && renderRemoteStartModal()}
      </div>
    </div>
  );
};
export default Headsets;